import PropTypes from 'prop-types';
import InputAdornment from '@material-ui/core/InputAdornment';
import CountryMenu from '@cont/Login/CountryMenu';
import Field from '@base/Form/Field';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { Fonts, Spacings, mediaQuery } from '@styles/index';
import styled from '@emotion/styled';
import NumberField from '@base/NumberField/NumberField';
import P2 from '@base/P2/P2';
import TextField from '@base/TextField/TextField';
import {
  isRequired,
  isLengthMatchError,
  isValidEmail
} from '@cont/OfflineCentres/EnquiryPage/utils';

const renderErrorMessage = ({ message }) => {
  return <Error>{message}</Error>;
};

const FormContainer = styled.form`
  width: 100%;
  margin-top: 0px;
  ${mediaQuery.aboveMobile} {
    margin-top: 0px;
    & > div:nth-child(1) {
      margin: 0;
    }
  }
`;

const FieldWrapper = styled.div`
  width: 416px;
  .MuiInputBase-root {
    height: ${Spacings.SPACING_14B};
    &.Mui-disabled {
      height: ${Spacings.SPACING_14B};
      box-sizing: border-box !important;
      input {
        border-radius: ${Spacings.SPACING_2B};
      }
      ${mediaQuery.uptoMobile} {
        height: ${Spacings.SPACING_12B};
        box-sizing: border-box !important;
      }
    }
    .MuiInputBase-input,
    .MuiInputBase-input::placeholder {
      font-size: ${Fonts.SMALL};
      font-weight: ${Fonts.SEMIBOLD};
    }
    ${mediaQuery.uptoMobile} {
      height: ${Spacings.SPACING_12B};
    }
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const StyledTextField = styled(TextField)`
  width: 100%;
  margin-top: ${Spacings.SPACING_4B};
  font-size: ${Fonts.H6};
  line-height: 50%;
  &.MuiOutlinedInput-root {
    width: 100%;
    height: ${Spacings.SPACING_14B};
    border-radius: ${Spacings.SPACING_2B};
    ${mediaQuery.uptoMobile} {
      height: ${Spacings.SPACING_12B};
    }
    .MuiOutlinedInput-input {
      width: 100%;
      height: ${Spacings.SPACING_14B};
      padding: 0px ${Spacings.SPACING_14};
      ${mediaQuery.uptoMobile} {
        height: ${Spacings.SPACING_12B};
        padding: 0px ${Spacings.SPACING_3B};
      }
    }
  }
`;

const Error = styled(P2)`
  font-weight: ${Fonts.SEMIBOLD};
  color: var(--color-i-red);
  margin-top: ${Spacings.SPACING_2B};
`;

const StyledNumberField = styled(NumberField)`
  margin-top: ${Spacings.SPACING_4B};
  width: 100%;
  font-size: ${Fonts.H6};
  line-height: 50%;
  &.MuiOutlinedInput-root {
    width: 100%;
    height: ${Spacings.SPACING_14B};
    ${mediaQuery.uptoMobile} {
      height: ${Spacings.SPACING_12B};
    }
    .MuiOutlinedInput-input {
      width: 100%;
      height: ${Spacings.SPACING_14B};
      padding: 0px ${Spacings.SPACING_14};
      ${mediaQuery.uptoMobile} {
        height: ${Spacings.SPACING_12B};
        padding: 0px ${Spacings.SPACING_3B};
      }
    }
  }
`;

const EnquiryPersonalDetails = ({
  registrar,
  changeCountry,
  country,
  countries
}) => {
  const isLoggedIn = useMyInfo(getIsLoggedIn);

  return (
    <FormContainer>
      <FieldWrapper>
        <Field
          registrar={registrar}
          name="name"
          rules={[isRequired('Name is required')]}
          render={({ onChange, value, hasError }) => (
            <StyledTextField
              placeholder="Enter full name"
              value={value}
              onChange={(data) => onChange(data.target.value)}
              error={hasError}
              disabled={isLoggedIn && registrar.data?.name}
            />
          )}
          renderError={renderErrorMessage}
        />
      </FieldWrapper>
      <FieldWrapper>
        <Field
          registrar={registrar}
          name="phone"
          rules={[
            isRequired('Mobile number is required'),
            isLengthMatchError('Please enter a valid mobile number', 10)
          ]}
          render={({ onChange, value, hasError }) => (
            <StyledNumberField
              fullWidth
              startAdornment={
                <InputAdornment>
                  <CountryMenu
                    changeCountry={changeCountry}
                    country={country}
                    countries={countries}
                    disabled={isLoggedIn}
                  />
                </InputAdornment>
              }
              placeholder="Enter mobile number"
              onChange={(_, data) => onChange(data)}
              value={value}
              inputProps={{
                type: 'tel',
                maxLength: 10,
                required: true
              }}
              type="tel"
              error={hasError}
              disabled={isLoggedIn}
            />
          )}
          renderError={renderErrorMessage}
        />
      </FieldWrapper>
      <FieldWrapper>
        <Field
          registrar={registrar}
          name="email"
          rules={[
            isRequired('Email is required'),
            isValidEmail('Enter a valid email')
          ]}
          render={({ onChange, value, hasError }) => (
            <StyledTextField
              placeholder="Enter email address"
              value={value}
              onChange={(data) => onChange(data.target.value)}
              error={hasError}
            />
          )}
          renderError={renderErrorMessage}
        />
      </FieldWrapper>
    </FormContainer>
  );
};

EnquiryPersonalDetails.propTypes = {
  registrar: PropTypes.objectOf(PropTypes.any).isRequired,
  changeCountry: PropTypes.func.isRequired,
  country: PropTypes.objectOf(PropTypes.any).isRequired,
  countries: PropTypes.arrayOf(PropTypes.any).isRequired
};

export default EnquiryPersonalDetails;
